import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH, VDJ_PRESETS } from './lib/vdjLook.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { coverFrame } from './lib/glass-sets.js';
import { RAMP16, RAMP10, EDGE, EDGE_BY_BIN, EDGE_THRESHOLD, gridFor, gridState, ROLL_SPAN, ROLL_DUR, ROLL_STEPS, ROLL_TOTAL, nextScene, MODES } from './lib/glyph-grid.js';

// Contrat du rig partagé : interdit. La scène source a sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'glyphs',
  family: 'shader',
  danceability: 0.86,
  energyLevel: 0.80,
  bpmMin: 110,
  bpmMax: 180,
  movement: 0.80,
  tonality: 0.40,
  enabled: true
};

// GLYPHS : tout l'écran en CARACTÈRES, comme de l'art ASCII géant. Une scène
// 3D (tunnel de polygones, sphère déformée par les basses, pochette en mode
// couvrir, surface d'eau en plongée, titre en volume) est rendue hors écran en
// 320 x 180 puis convertie en une grille de 160 x 90 caractères dont la forme
// suit la LUMINANCE ET L'ORIENTATION DES CONTOURS. Techno, électro, drum'n'bass,
// IDM, bass music. Règles R1 à R19, preset party de vdjLook, adapt.js, horloge
// de scène partagée (« R20 »).
//
// LA CONVERSION, le détail décisif : pour chaque cellule, le gradient de
// luminance entre les cellules voisines ; s'il est fort, un glyphe qui suit la
// DIRECTION du contour ( ) > \ - / < ( _ , choisi par secteur de 22,5° du
// gradient : lib/glyph-grid.js), sinon un glyphe de la rampe de densité (16,
// du point au bloc plein ; 10 au tier 0). L'atlas est un canvas monospace
// rendu À LA TAILLE EXACTE d'une cellule (R1), échantillonné en Nearest, et une
// cellule ne lit jamais la voisine. Couleur : celle de la source (COULEUR),
// un vert ou ambre de terminal (TERMINAL), blanc sur noir avec l'accent de la
// palette sur les contours (PRINT).
//
// LA MUSIQUE : une vague de luminosité traverse la grille à chaque temps (sens
// alterné), séparation RGB vdj ; bands.high fait scintiller des caractères
// isolés (glitch de terminal) ; MESURE : mouvement de caméra dans la scène
// source (vdjCameraKick), mesure forte (toutes les 4, intensité > 0,6) en
// 120 x 68 ; PHRASE : scène source suivante et mode de couleur suivant, les
// caractères DÉROULENT en vague (3 à 5 glyphes aléatoires par cellule avant la
// cible, l'ancienne scène gardée dans une seconde cible) ; MONTÉE
// (director.mode build) : 220 x 124 ; DROP (adapt.dropReady) : 64 x 36 pendant
// une mesure, flash. SCÈNE (R20, 40 s) : au début d'un cycle, la prochaine
// scène est la pochette (le titre sans pochette).
//
// Repli sans pochette : la scène pochette est retirée du tirage. Pause : gel.
// Debug : ?glyphsrc=tunnel|sphere|cover|waves|title|test (test : disque et
// diagonale, pour vérifier les contours), ?glyphmode=color|terminal|print,
// ?glyphcells=<colonnes>, ?glyphdebug=index|roll (sortie : indice de glyphe /
// état de déroulement en gris), ?glyphprobe=1.

const TIER = { 0: { ramp: RAMP10 }, 1: { ramp: RAMP16 }, 2: { ramp: RAMP16 } };
const SRC_W = 320, SRC_H = 180;
const SCENE_S = 40;
const GLYPHS_PER_SCENE = ['tunnel', 'sphere', 'cover', 'waves', 'title', 'test'];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}

// ------------------------------------------------------------------ GLSL
const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// la conversion : une cellule = un glyphe
const CONVERT_FRAGMENT = `
  precision highp float;
  uniform sampler2D tSrc, tPrev, tAtlas;
  uniform vec2 uGrid;                 // colonnes, lignes
  uniform float uAtlasN, uRampN, uEdgeThresh, uEdgeMap[16];
  uniform float uMode, uDebug, uTime, uHigh, uGain, uFlash;
  uniform vec3 uTermColor, uAccent;
  uniform vec4 uWave;                 // position (0..1 le long de x), largeur, gain, sens (±1)
  uniform vec4 uRoll;                 // t depuis le début, dir.x, dir.y, actif
  uniform float uAspect;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  const vec3 LW = vec3(0.2126, 0.7152, 0.0722);
  float hash2(vec2 p) { return fract(sin(p.x * 127.1 + p.y * 311.7) * 43758.5453); }
  float lumAt(sampler2D t, vec2 uv) { return dot(texture2D(t, uv).rgb, LW); }
  void main() {
    vec2 cell = floor(vUv * uGrid);
    vec2 cuv = (cell + 0.5) / uGrid;            // le centre de la cellule
    vec2 f = fract(vUv * uGrid);                // la position dans la cellule
    // le déroulement : quelle scène, et déroule-t-on ?
    float phase = 1.0, step_ = -1.0;
    if (uRoll.w > 0.5) {
      vec2 d = uRoll.yz;
      float ext = (abs(d.x) + abs(d.y)) * 0.5;
      float along = ((dot(cuv - 0.5, d)) / ext + 1.0) * 0.5;
      float delay = along * ${ROLL_SPAN.toFixed(3)} + hash2(cuv * vec2(97.0, 57.0)) * 0.08;
      float p = (uRoll.x - delay) / ${ROLL_DUR.toFixed(3)};
      phase = clamp(p, 0.0, 1.0);
      if (p > 0.0 && p < 1.0) step_ = floor(p * ${ROLL_STEPS.toFixed(1)});
    }
    bool useNew = phase > 0.0;
    vec3 col; float gx, gy;
    vec2 e = 0.5 / uGrid;                       // une demi-cellule : le gradient entre voisines
    if (useNew) { col = texture2D(tSrc, cuv).rgb; gx = lumAt(tSrc, cuv + vec2(e.x, 0.0)) - lumAt(tSrc, cuv - vec2(e.x, 0.0)); gy = lumAt(tSrc, cuv + vec2(0.0, e.y)) - lumAt(tSrc, cuv - vec2(0.0, e.y)); }
    else { col = texture2D(tPrev, cuv).rgb; gx = lumAt(tPrev, cuv + vec2(e.x, 0.0)) - lumAt(tPrev, cuv - vec2(e.x, 0.0)); gy = lumAt(tPrev, cuv + vec2(0.0, e.y)) - lumAt(tPrev, cuv - vec2(0.0, e.y)); }
    float lum = dot(col, LW);
    // la vague de luminosité du temps
    float wx = uWave.w > 0.0 ? cuv.x : 1.0 - cuv.x;
    float wave = 1.0 + uWave.z * exp(-pow((wx - uWave.x) / max(uWave.y, 1e-3), 2.0));
    lum = clamp(lum * wave, 0.0, 1.0);
    // le glyphe : contour orienté, sinon densité
    float mag = length(vec2(gx, gy));
    float glyph;
    bool edge = mag > uEdgeThresh;
    if (edge) {
      float a = atan(gy, gx);
      float bin = mod(floor(a / (3.14159265 / 8.0) + 0.5) + 16.0, 16.0);
      glyph = uRampN + uEdgeMap[int(bin)];
    } else glyph = floor(lum * (uRampN - 1.0) + 0.5);
    // le déroulement : un glyphe au hasard, lumineux
    if (step_ >= 0.0) { glyph = floor(hash2(cell + step_ * 13.7) * uAtlasN); lum = max(lum, 0.6); }
    // le glitch de terminal sur les aigus : des caractères ISOLÉS (quelques dizaines à la fois, au-dessus de 0,35 d'aigus)
    float g = hash2(cell * 0.37 + floor(uTime * 24.0) * 0.113);
    bool glitch = g < max(0.0, uHigh - 0.35) * 0.012;
    if (glitch) { glyph = floor(hash2(cell + floor(uTime * 24.0)) * uAtlasN); lum = 1.0; }
    // l'atlas, à la taille exacte de la cellule, Nearest : rien ne déborde
    float a = texture2D(tAtlas, vec2((glyph + f.x) / uAtlasN, f.y)).a;
    // la couleur
    vec3 out_;
    if (uMode < 0.5) {
      // COULEUR : la teinte de la source, portée par la luminance (une source sombre reste colorée)
      vec3 chroma = col / max(max(col.r, max(col.g, col.b)), 0.08);
      out_ = chroma * (0.3 + 0.7 * lum);
    } else if (uMode < 1.5) {
      out_ = uTermColor * (0.3 + 0.7 * lum);
    } else {
      out_ = (edge ? uAccent : vec3(1.0)) * (0.3 + 0.7 * lum);
    }
    if (glitch) out_ = mix(out_, vec3(1.0), 0.6);
    if (uDebug > 0.5 && uDebug < 1.5) { gl_FragColor = vec4(vec3(glyph / 255.0), 1.0); return; }
    if (uDebug > 1.5) { gl_FragColor = vec4(vec3(step_ >= 0.0 ? 0.5 : phase), 1.0); return; }
    // le masque safeZone (R3 : multiplie) et le flash
    vec2 s = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(0.2, 1.0, smoothstep(0.0, uSafeFeather, sd));
    vec3 rgb = out_ * a * att * uGain + uFlash * a;
    gl_FragColor = vec4(rgb, 1.0);
  }
`;
const DISPLAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;
const LUM_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { vec3 c = texture2D(uTex, vUv).rgb; gl_FragColor = vec4(vec3(pow(dot(c, vec3(0.2126, 0.7152, 0.0722)), 1.0 / 2.2)), 1.0); }
`;
// --- les scènes sources ------------------------------------------------------
// le tunnel : un prisme octogonal vu de l'intérieur, anneaux clairs qui défilent, arêtes des polygones claires
const TUNNEL_VERTEX = `
  varying vec3 vP; varying vec2 vUv2;
  void main() { vP = position; vUv2 = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
const TUNNEL_FRAGMENT = `
  precision mediump float;
  uniform float uScroll, uPulse; uniform vec3 uColA, uColB;
  varying vec3 vP; varying vec2 vUv2;
  void main() {
    float z = vP.z * 0.5 + uScroll;
    float ring = smoothstep(0.42, 0.5, abs(fract(z) - 0.5));                 // des anneaux nets
    float seg = fract(vUv2.x * 8.0);                                          // 8 faces : les arêtes
    float edgeL = smoothstep(0.06, 0.0, min(seg, 1.0 - seg));
    float fog = clamp(1.0 + vP.z * 0.06, 0.0, 1.0);
    vec3 col = mix(uColA, uColB, fract(z * 0.25)) * (0.15 + 0.85 * ring) + vec3(1.0) * edgeL * 0.9;
    col *= fog * (0.8 + 0.4 * uPulse);
    gl_FragColor = vec4(col, 1.0);
  }
`;
// la sphère : déformée par les basses (bosses analytiques), éclairée en lambert avec normale analytique
const SPHERE_VERTEX = `
  uniform float uTime, uBass;
  varying float vL; varying vec3 vN;
  float bump(vec3 p) { return 1.0 + uBass * (0.18 * sin(p.x * 5.0 + uTime * 1.7) * sin(p.y * 4.0 - uTime) * sin(p.z * 6.0 + uTime * 0.7) + 0.06 * sin(p.y * 11.0 + uTime * 3.0)); }
  void main() {
    vec3 n = normalize(position);
    float r = bump(n);
    // la normale par différences finies sur la sphère
    vec3 t1 = normalize(cross(n, abs(n.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0)));
    vec3 t2 = cross(n, t1);
    float h = 0.01;
    vec3 pa = normalize(n + t1 * h) * bump(normalize(n + t1 * h));
    vec3 pb = normalize(n + t2 * h) * bump(normalize(n + t2 * h));
    vec3 p = n * r;
    vec3 N = normalize(cross(pa - p, pb - p));
    if (dot(N, n) < 0.0) N = -N;
    vN = normalize(mat3(modelMatrix) * N);
    vec3 L = normalize(vec3(0.6, 0.8, 0.9));
    vL = max(dot(vN, L), 0.0);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`;
const SPHERE_FRAGMENT = `
  precision mediump float;
  uniform vec3 uColA, uColB;
  varying float vL; varying vec3 vN;
  void main() {
    vec3 col = mix(uColB, uColA, vL) * (0.08 + 0.92 * vL);
    float spec = pow(max(dot(normalize(vN), normalize(vec3(0.6, 0.8, 0.9) + vec3(0.0, 0.0, 1.0))), 0.0), 40.0);
    gl_FragColor = vec4(col + vec3(1.0) * spec * 0.8, 1.0);
  }
`;
// les ondes : un plan vu en plongée, hauteur = somme de sinus, normale analytique, lambert + reflet
const WAVES_VERTEX = `
  uniform float uTime, uBass;
  varying float vL; varying float vSpec;
  void main() {
    vec2 p = position.xy;
    float t = uTime;
    float h = 0.0, dx = 0.0, dy = 0.0;
    vec3 w[3];
    w[0] = vec3(1.0, 0.3, 1.2); w[1] = vec3(-0.6, 1.0, 2.1); w[2] = vec3(0.4, -0.8, 3.3);
    for (int i = 0; i < 3; i++) {
      float k = w[i].z, ph = dot(p, w[i].xy) * k - t * (1.2 + 0.4 * float(i));
      float a = (0.10 - 0.02 * float(i)) * (1.0 + uBass);
      h += a * sin(ph); dx += a * k * w[i].x * cos(ph); dy += a * k * w[i].y * cos(ph);
    }
    float r = length(p); float ph2 = r * 6.0 - t * 3.0; h += 0.05 * uBass * sin(ph2);
    vec3 N = normalize(vec3(-dx, -dy, 1.0));
    vec3 L = normalize(vec3(0.3, -0.6, 0.75));
    vL = max(dot(N, L), 0.0);
    vec3 V = normalize(cameraPosition - vec3(p, h));
    vSpec = pow(max(dot(N, normalize(L + V)), 0.0), 60.0);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, h, 1.0);
  }
`;
const WAVES_FRAGMENT = `
  precision mediump float;
  uniform vec3 uColA, uColB;
  varying float vL; varying float vSpec;
  void main() { gl_FragColor = vec4(mix(uColB * 0.25, uColA, vL) * (0.1 + 0.9 * vL) + vec3(1.0) * vSpec, 1.0); }
`;
// la pochette en mode couvrir (R18), Ken Burns
const COVER_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uCover; uniform float uHas; uniform vec4 uFrame; uniform vec3 uColA, uColB;
  varying vec2 vUv;
  void main() {
    vec2 uv = uFrame.zw + (vUv - 0.5) * uFrame.xy;
    vec3 c = uHas > 0.5 ? texture2D(uCover, uv).rgb : mix(uColA, uColB, vUv.y);
    gl_FragColor = vec4(c, 1.0);
  }
`;
// la mire de test : un disque clair et une diagonale (contours de toutes directions)
const TEST_FRAGMENT = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vec2 p = (vUv - 0.5) * vec2(16.0 / 9.0, 1.0);
    float disc = step(length(p + vec2(0.45, 0.0)), 0.32);
    float diag = step(abs(p.x - p.y - 0.5), 0.05);
    float grad = clamp((p.x - 0.1) * 2.0, 0.0, 1.0) * step(0.1, p.x) * step(p.x, 0.6) * step(abs(p.y + 0.3), 0.12);
    gl_FragColor = vec4(vec3(max(max(disc * 0.9, diag * 0.9), grad)), 1.0);
  }
`;

// =============================================================================

export class GlyphsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._L = TIER[this._tier] || TIER[1];
    const src = urlParam('glyphsrc'); this._forceSrc = GLYPHS_PER_SCENE.includes(src) ? src : null;
    const mode = urlParam('glyphmode'); this._forceMode = MODES.includes(mode) ? mode : null;
    const cells = parseInt(urlParam('glyphcells'), 10); this._forceCells = Number.isFinite(cells) && cells >= 16 ? cells : null;
    const dbg = urlParam('glyphdebug'); this._debug = dbg === 'index' ? 1 : dbg === 'roll' ? 2 : 0;
    this._post = new VdjPost({ bloomStrength: 0.15, bloomThreshold: 0.85, grain: 0.02, vignette: 0.3, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._postValue = this._post.update(1);
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    if (urlParam('glyphprobe') === '1' && typeof window !== 'undefined') window.__glyphs = this;
  }

  get postProcessing() { return this._postValue; }

  init(scene) {
    this._sceneRef = scene;
    this._size = new THREE.Vector2(1280, 720);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10); this._ortho.position.z = 1;
    // les deux cibles source (courante, précédente pour le déroulement)
    const srcOpts = { depthBuffer: true, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false };
    this._srcRT = new THREE.WebGLRenderTarget(SRC_W, SRC_H, srcOpts);
    this._prevRT = new THREE.WebGLRenderTarget(SRC_W, SRC_H, srcOpts);
    // la scène source et sa caméra
    this._src = new THREE.Scene();
    this._cam = new THREE.PerspectiveCamera(55, 16 / 9, 0.05, 60);
    this._pal = { a: new THREE.Vector3(1, 0.3, 0.7), b: new THREE.Vector3(0.2, 0.6, 1), c: new THREE.Vector3(1, 0.85, 0.3) };
    this._buildScenes();
    // l'atlas et la conversion
    this._atlasCache = new Map(); this._atlas = null; this._atlasKey = '';
    this._ramp = this._L.ramp;
    this._glyphList = this._ramp.concat(EDGE);
    this._CU = { tSrc: { value: this._srcRT.texture }, tPrev: { value: this._prevRT.texture }, tAtlas: { value: null },
      uGrid: { value: new THREE.Vector2(160, 90) }, uAtlasN: { value: this._glyphList.length }, uRampN: { value: this._ramp.length }, uEdgeThresh: { value: EDGE_THRESHOLD },
      uEdgeMap: { value: EDGE_BY_BIN.slice() }, uMode: { value: 0 }, uDebug: { value: this._debug }, uTime: { value: 0 }, uHigh: { value: 0 }, uGain: { value: 1 }, uFlash: { value: 0 },
      uTermColor: { value: new THREE.Vector3(0.3, 1, 0.4) }, uAccent: { value: new THREE.Vector3(1, 0.3, 0.6) },
      uWave: { value: new THREE.Vector4(0, 0.12, 0, 1) }, uRoll: { value: new THREE.Vector4(0, 1, 0, 0) },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._convScene = new THREE.Scene();
    const cq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._CU, vertexShader: QUAD_VERTEX, fragmentShader: CONVERT_FRAGMENT, depthTest: false, depthWrite: false }));
    cq.frustumCulled = false; this._convScene.add(cq);
    // la cible privée (Nearest : les glyphes restent nets), l'affichage, la mesure R10
    this._target = null;
    this._dispU = { uTex: { value: null } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    this._display.onBeforeRender = (renderer) => this._render(renderer);
    scene.add(this._display);
    this._lumTarget = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false });
    this._lumPixels = new Uint8Array(16 * 16 * 4);
    this._lumU = { uTex: { value: null } };
    this._lumScene = new THREE.Scene();
    const lq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._lumU, vertexShader: QUAD_VERTEX, fragmentShader: LUM_FRAGMENT, depthTest: false, depthWrite: false }));
    lq.frustumCulled = false; this._lumScene.add(lq);
    this._lumAge = 0; this._lumMeasured = 0.2; this._lumPending = false; this._lumAvg = 0.2; this._gain = 1; this._gainRendered = 1;

    // l'état
    this._adapt = new Adapt(); this._a = this._adapt.get();
    this._sceneClock = new SceneClock({ period: SCENE_S, start: 0 });
    this._time = 0; this._paused = false; this._rendered = false;
    this._scene = this._forceSrc || 'tunnel'; this._pendingScene = null; this._rollT0 = -99; this._rollDir = new THREE.Vector2(1, 0);
    this._modeIdx = 0; this._termAmber = false; this._mustAnchor = false; this._anchorSeen = false; this._firstPhrase = true;
    this._bass = 0.3; this._high = 0.3; this._pulseEnv = 0; this._waveDir = 1; this._beatIdx = 0; this._barIdx = 0;
    this._dropUntil = -1; this._dropAt = null; this._strongBarUntil = -1; this._kick = 0;
    this._gridState = 'normal'; this._flash = 0;
    this._coverUrl = undefined; this._coverTex = null; this._coverAspect = 1; this._paletteSeen = undefined; this._titleSeen = undefined;
    this._stats = { beats: 0, bars: 0, phrases: 0, scenes: 0, drops: 0, rolls: 0 };
    this._applyPalette(null);
    this._setScene(this._scene);
  }

  // --- les scènes sources -------------------------------------------------------
  _buildScenes() {
    const P = this._pal;
    this._groups = {};
    // TUNNEL : un cylindre octogonal vu de l'intérieur
    const tg = new THREE.Group();
    this._tunnelU = { uScroll: { value: 0 }, uPulse: { value: 0 }, uColA: { value: P.a }, uColB: { value: P.b } };
    const tgeo = new THREE.CylinderGeometry(1.6, 1.6, 60, 8, 1, true); tgeo.rotateX(Math.PI / 2);
    const tunnel = new THREE.Mesh(tgeo, new THREE.ShaderMaterial({ uniforms: this._tunnelU, vertexShader: TUNNEL_VERTEX, fragmentShader: TUNNEL_FRAGMENT, side: THREE.BackSide }));
    tunnel.frustumCulled = false; tg.add(tunnel); this._groups.tunnel = tg;
    // SPHÈRE
    const sg = new THREE.Group();
    this._sphereU = { uTime: { value: 0 }, uBass: { value: 0 }, uColA: { value: P.a }, uColB: { value: P.b } };
    const sph = new THREE.Mesh(new THREE.SphereGeometry(1, 96, 64), new THREE.ShaderMaterial({ uniforms: this._sphereU, vertexShader: SPHERE_VERTEX, fragmentShader: SPHERE_FRAGMENT }));
    sph.frustumCulled = false; sg.add(sph); this._sphere = sph; this._groups.sphere = sg;
    // ONDES
    const wg = new THREE.Group();
    this._wavesU = { uTime: { value: 0 }, uBass: { value: 0 }, uColA: { value: P.b }, uColB: { value: P.a } };
    const wmesh = new THREE.Mesh(new THREE.PlaneGeometry(12, 12, 96, 96), new THREE.ShaderMaterial({ uniforms: this._wavesU, vertexShader: WAVES_VERTEX, fragmentShader: WAVES_FRAGMENT, side: THREE.DoubleSide }));
    wmesh.frustumCulled = false; wg.add(wmesh); this._groups.waves = wg;
    // POCHETTE (quad plein écran)
    const cg = new THREE.Group();
    this._coverU = { uCover: { value: null }, uHas: { value: 0 }, uFrame: { value: new THREE.Vector4(1, 1, 0.5, 0.5) }, uColA: { value: P.a }, uColB: { value: P.b } };
    const cq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._coverU, vertexShader: QUAD_VERTEX, fragmentShader: COVER_FRAGMENT, depthTest: false, depthWrite: false }));
    cq.frustumCulled = false; cg.add(cq); this._groups.cover = cg;
    // TITRE : des voxels instanciés d'après le texte (canvas), éclairés
    const ttg = new THREE.Group();
    this._titleMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.45, metalness: 0.1 });
    this._titleMesh = null; ttg.add(new THREE.DirectionalLight(0xffffff, 2.2).translateX(2).translateY(3).translateZ(4)); ttg.add(new THREE.AmbientLight(0xffffff, 0.25));
    this._titleGroup = ttg; this._groups.title = ttg;
    // TEST : la mire
    const xg = new THREE.Group();
    const xq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ vertexShader: QUAD_VERTEX, fragmentShader: TEST_FRAGMENT, depthTest: false, depthWrite: false }));
    xq.frustumCulled = false; xg.add(xq); this._groups.test = xg;
    for (const k in this._groups) { this._groups[k].visible = false; this._src.add(this._groups[k]); }
  }
  // le titre en voxels : le texte rendu dans un canvas, échantillonné en grille, un cube par point
  _buildTitle(title) {
    if (this._titleMesh) { this._titleGroup.remove(this._titleMesh); this._titleMesh.geometry.dispose(); this._titleMesh = null; }
    const text = (title || 'HALOFLOOR').toUpperCase().slice(0, 18);
    if (typeof document === 'undefined') return;
    const W = 192, H = 40, cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d'); ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    let fs = 34; ctx.font = `bold ${fs}px "Helvetica Neue", Arial, sans-serif`;
    while (ctx.measureText(text).width > W - 6 && fs > 8) { fs -= 1; ctx.font = `bold ${fs}px "Helvetica Neue", Arial, sans-serif`; }
    ctx.fillText(text, W / 2, H / 2 + 1);
    const px = ctx.getImageData(0, 0, W, H).data;
    const cells = [];
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (px[(y * W + x) * 4] > 128) cells.push([x, y]);
    if (!cells.length) return;
    const s = 3.6 / W;                                                  // la largeur du titre : 3,6 unités
    const geo = new THREE.BoxGeometry(s, s, s * 3);
    const mesh = new THREE.InstancedMesh(geo, this._titleMat, cells.length);
    const m = new THREE.Matrix4();
    cells.forEach(([x, y], i) => { m.makeTranslation((x - W / 2 + 0.5) * s, (H / 2 - y - 0.5) * s, 0); mesh.setMatrixAt(i, m); });
    mesh.instanceMatrix.needsUpdate = true; mesh.frustumCulled = false;
    this._titleMesh = mesh; this._titleGroup.add(mesh);
  }
  _setScene(name) {
    for (const k in this._groups) this._groups[k].visible = k === name;
    this._scene = name;
    if (name === 'cover' || name === 'title') this._anchorSeen = true;
  }
  _applyPalette(pal) {
    if (pal === this._paletteSeen && pal !== null) return;
    this._paletteSeen = pal;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2bd6, 0x2bd8ff, 0xffd23f];
    const put = (u, hex) => { const c = vdjColor(hex); u.set(c.r, c.g, c.b); };
    put(this._pal.a, v[0]); put(this._pal.b, v[1 % v.length]); put(this._pal.c, v[2 % v.length]);
    this._CU.uAccent.value.copy(this._pal.a);
  }
  _loadCover(profile) {
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._coverU.uHas.value = 0;
    if (!profile.artworkUrl) return;
    const url = profile.artworkUrl;
    loadArtworkTexture(url, { mipmaps: false, wrap: THREE.ClampToEdgeWrapping }, (tex) => {
      if (!this._sceneRef || url !== this._coverUrl) { tex.dispose(); return; }
      this._coverTex = tex; this._coverU.uCover.value = tex; this._coverU.uHas.value = 1;
      const img = tex.image; this._coverAspect = img && img.width ? img.width / img.height : 1;
    }, () => {});
  }
  // l'atlas : chaque glyphe dans une case de la taille exacte d'une cellule à l'écran (R1)
  _ensureAtlas(cellW, cellH) {
    const cw = Math.max(2, Math.ceil(cellW)), ch = Math.max(2, Math.ceil(cellH));
    const key = cw + 'x' + ch;
    if (key === this._atlasKey) return;
    this._atlasKey = key;
    let tex = this._atlasCache.get(key);
    if (!tex) {
      const n = this._glyphList.length;
      const cv = document.createElement('canvas'); cv.width = cw * n; cv.height = ch;
      const ctx = cv.getContext('2d');
      ctx.clearRect(0, 0, cv.width, ch);
      ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = `bold ${Math.round(ch * 1.05)}px "DejaVu Sans Mono", Menlo, Consolas, "Liberation Mono", monospace`;
      this._glyphList.forEach((g, i) => {
        if (g === ' ') return;
        if (g === '█') { ctx.fillRect(i * cw, 0, cw, ch); return; }
        // le canvas a y vers le bas, la texture y vers le haut : on dessine inversé pour que « _ » soit en bas
        ctx.save(); ctx.translate(i * cw + cw / 2, ch / 2); ctx.scale(1, -1); ctx.fillText(g, 0, 0); ctx.restore();
      });
      tex = new THREE.CanvasTexture(cv); tex.minFilter = THREE.NearestFilter; tex.magFilter = THREE.NearestFilter; tex.generateMipmaps = false; tex.flipY = false; tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
      this._atlasCache.set(key, tex);
    }
    this._CU.tAtlas.value = tex;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState != null && profile.playbackState !== 'PLAYING';
    this._applyPalette(profile.artworkPalette);
    this._loadCover(profile);
    const title = profile.title || null;
    if (title !== this._titleSeen) { this._titleSeen = title; this._buildTitle(title); }
    const dt = this._paused ? 0 : Math.min(Math.max(deltaTime, 0), 0.1);
    const a = this._a = this._adapt.update(dt, profile);
    this._sceneClock.update(dt);
    if (this._paused) return;
    this._time += dt;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 128, beatS = 60 / bpm;
    const bands = profile.director && profile.director.bands;
    const real = bands && (bands.low > 0 || bands.high > 0);
    this._bass += ((real ? clamp01(bands.low) : 0.45 + 0.35 * Math.sin(this._time * 1.1)) - this._bass) * (1 - Math.exp(-dt / 0.12));
    this._high += ((real ? clamp01(bands.high) : 0.3 + 0.3 * Math.sin(this._time * 2.3)) - this._high) * (1 - Math.exp(-dt / 0.1));
    const hasCover = this._coverU.uHas.value > 0.5;
    const building = !!(profile.director && profile.director.mode === 'build');

    // SCÈNE (R20) : au début d'un cycle sans pochette ni titre vus, la prochaine scène est l'ancre
    if (this._sceneClock.wrapped) { this._stats.scenes++; if (!this._anchorSeen) this._mustAnchor = true; this._anchorSeen = false; }
    // PHRASE : scène suivante (déroulement) et mode de couleur suivant
    if (a.phrase && this._forceSrc == null) {
      this._stats.phrases++;
      const next = nextScene(this._scene, hasCover, this._mustAnchor, Math.random());
      this._mustAnchor = false;
      this._pendingScene = next;
      if (this._forceMode == null) { this._modeIdx = (this._modeIdx + 1) % MODES.length; if (MODES[this._modeIdx] === 'terminal') this._termAmber = !this._termAmber; }
    } else if (a.phrase && this._forceMode == null) { this._modeIdx = (this._modeIdx + 1) % MODES.length; }
    // MESURE : à-coup de caméra, mesure forte toutes les 4 (intensité > 0,6)
    if (a.bar) { this._stats.bars++; this._barIdx++; this._kick = 1; if (this._barIdx % 4 === 0 && a.intensity > 0.6) this._strongBarUntil = this._time + 4 * beatS; }
    this._kick *= Math.exp(-dt / 0.3);
    // TEMPS : la vague de luminosité, sens alterné
    if (a.beat) { this._stats.beats++; this._beatIdx++; this._waveDir = -this._waveDir; }
    const wavePos = clamp01(a.beatPhase);
    this._CU.uWave.value.set(wavePos, 0.10, 0.9 * a.pulse * Math.exp(-a.beatPhase * 2.0), this._waveDir);
    // DROP : cellules géantes une mesure, flash
    if (a.drop && a.dropReady && this._time >= this._dropUntil) {
      this._stats.drops++; this._dropUntil = this._time + 4 * beatS; this._dropAt = this._time;
      if (profile.director && this._comfort.requestFlash(this._time)) profile.director.flash = VDJ_DROP_FLASH;
      this._flash = 0.6;
    }
    this._flash *= Math.exp(-dt / 0.12);
    // la grille
    const state = gridState({ dropBarsLeft: this._time < this._dropUntil ? 1 : 0, building, strongBar: this._time < this._strongBarUntil });
    this._gridState = state;
    let [cols, rows] = gridFor(state, this._tier);
    if (this._forceCells) { cols = this._forceCells; rows = Math.max(4, Math.round(cols * 9 / 16)); }
    this._CU.uGrid.value.set(cols, rows);

    // la scène source : caméra et animation
    // l'à-coup de la mesure : k (1 -> 0 en 0,3 s) pour le zoom / l'avance, le roulis de vdjCameraKick sur les mesures impaires
    const k = this._kick;
    const kick = vdjCameraKick(a.beatPhase != null ? ((this._beatIdx % 4) + a.beatPhase) / 4 : 0, this._barIdx, 4);
    const t = this._time, mot = a.motion;
    const cam = this._cam;
    cam.aspect = 16 / 9;
    switch (this._scene) {
      case 'tunnel':
        this._tunnelU.uScroll.value += dt * (1.2 + 2.0 * mot); this._tunnelU.uPulse.value = a.pulse * Math.exp(-a.beatPhase * 3);
        cam.position.set(Math.sin(t * 0.7) * 0.35 + k * 0.2, Math.cos(t * 0.5) * 0.3, 0); cam.lookAt(Math.sin(t * 0.7 + 1) * 0.3, Math.cos(t * 0.5 + 1) * 0.2, -6); cam.fov = 70 + 10 * k; break;
      case 'sphere':
        this._sphereU.uTime.value = t; this._sphereU.uBass.value = 0.35 + 0.65 * this._bass;
        this._sphere.rotation.y = t * 0.6 * mot + k * 0.4; this._sphere.rotation.x = Math.sin(t * 0.3) * 0.4;
        cam.position.set(Math.sin(t * 0.2) * 0.6, 0.3, 2.6 - 0.3 * k); cam.lookAt(0, 0, 0); cam.fov = 55; break;
      case 'waves':
        this._wavesU.uTime.value = t * (0.6 + 0.8 * mot); this._wavesU.uBass.value = this._bass;
        cam.position.set(Math.sin(t * 0.15) * 1.5, -3.2, 2.4 + 0.4 * k); cam.lookAt(0, 0.5 + Math.sin(t * 0.21), 0); cam.fov = 60; break;
      case 'title':
        if (this._titleMesh) { this._titleMesh.rotation.y = Math.sin(t * 0.5) * 0.9 + k * 0.3; this._titleMesh.rotation.x = Math.sin(t * 0.33) * 0.3; }
        cam.position.set(0, 0.2, 3.0 - 0.4 * k); cam.lookAt(0, 0, 0); cam.fov = 55; break;
      case 'cover': {
        const fr = coverFrame(16 / 9, this._coverAspect, t * (0.6 + mot));
        const zoom = 1 - 0.08 * k;                                  // l'à-coup de la mesure : un léger zoom
        this._coverU.uFrame.value.set(fr.visW * zoom, fr.visH * zoom, fr.cx, fr.cy); break;
      }
    }
    if (this._scene !== 'cover') cam.rotateZ(kick.roll);
    cam.updateProjectionMatrix();
    // la conversion
    const CU = this._CU;
    const modeName = this._forceMode || MODES[this._modeIdx];
    CU.uMode.value = MODES.indexOf(modeName);
    CU.uTermColor.value.set(this._termAmber ? 1.0 : 0.35, this._termAmber ? 0.7 : 1.0, this._termAmber ? 0.15 : 0.45);
    CU.uTime.value = t; CU.uHigh.value = this._high; CU.uFlash.value = this._flash;
    // R10 (party : 0,45), mesuré sur la cible privée
    this._lumAge += dt;
    if (this._lumAge >= 0.5) { this._lumAge = 0; this._lumPending = true; }
    this._lumAvg += (this._lumMeasured - this._lumAvg) * Math.min(1, dt / 1.0);
    const gt = Math.min(1, 0.9 * 0.45 / Math.max(1e-3, this._lumAvg));
    this._gain += (gt - this._gain) * Math.min(1, dt / 0.5);
    CU.uGain.value = this._gain;
    this._postValue = this._post.update(vdjBeatChroma(a.beatPhase, beatS));
    this._beatS = beatS;
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    if (prev) this._size.set(prev.width, prev.height); else renderer.getDrawingBufferSize(this._size);
    const w = Math.max(16, Math.round(this._size.x)), h = Math.max(16, Math.round(this._size.y));
    if (!this._target || this._target.width !== w || this._target.height !== h) {
      if (this._target) this._target.dispose();
      this._target = new THREE.WebGLRenderTarget(w, h, { depthBuffer: false, stencilBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
      this._dispU.uTex.value = this._target.texture; this._lumU.uTex.value = this._target.texture;
    }
    if (this._paused && this._rendered) return;
    const cc = new THREE.Color(); renderer.getClearColor(cc); const ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setClearColor(0x000000, 1);
    // le changement de scène : l'ancienne rendue une dernière fois dans la cible « précédente », puis le déroulement commence
    if (this._pendingScene) {
      renderer.setRenderTarget(this._prevRT); renderer.clear(true, true, false); renderer.render(this._src, this._scene === 'cover' || this._scene === 'test' ? this._ortho : this._cam);
      this._setScene(this._pendingScene); this._pendingScene = null;
      this._rollT0 = this._time; this._stats.rolls++;
      const ang = Math.floor(Math.random() * 8) * Math.PI / 4; this._rollDir.set(Math.cos(ang), Math.sin(ang));
      if (Math.abs(this._rollDir.x) < 1e-6) this._rollDir.x = 0; if (Math.abs(this._rollDir.y) < 1e-6) this._rollDir.y = 0;
    }
    const rollT = this._time - this._rollT0;
    this._CU.uRoll.value.set(rollT, this._rollDir.x, this._rollDir.y, rollT < ROLL_TOTAL ? 1 : 0);
    // 1. la scène source en 320 x 180
    renderer.setRenderTarget(this._srcRT); renderer.clear(true, true, false);
    renderer.render(this._src, this._scene === 'cover' || this._scene === 'test' ? this._ortho : this._cam);
    // 2. la conversion dans la cible privée, avec l'atlas à la taille de la cellule
    const g = this._CU.uGrid.value;
    this._ensureAtlas(w / g.x, h / g.y);
    this._gainRendered = this._gain;
    renderer.setRenderTarget(this._target); renderer.render(this._convScene, this._ortho);
    this._rendered = true;
    if (this._lumPending) {
      this._lumPending = false;
      renderer.setRenderTarget(this._lumTarget); renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, 16, 16, this._lumPixels);
      let sum = 0; for (let i = 0; i < this._lumPixels.length; i += 4) sum += this._lumPixels[i];
      this._lumMeasured = sum / (256 * 255) / Math.max(0.05, this._gainRendered);
    }
    renderer.setRenderTarget(prev);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
  }

  getProbe() {
    const g = this._CU.uGrid.value;
    return { tier: this._tier, scene: this._scene, mode: this._forceMode || MODES[this._modeIdx], grid: [g.x, g.y], gridState: this._gridState, atlas: this._atlasKey, glyphs: this._glyphList.length, ramp: this._ramp.length,
      rollT: +(this._time - this._rollT0).toFixed(3), rolling: this._time - this._rollT0 < ROLL_TOTAL, rollDir: [this._rollDir.x, this._rollDir.y], intensity: +this._a.intensity.toFixed(3),
      lum: +this._lumMeasured.toFixed(3), gain: +this._gain.toFixed(3), hasCover: this._coverU.uHas.value, titleVoxels: this._titleMesh ? this._titleMesh.count : 0, stats: Object.assign({}, this._stats) };
  }
  // (sonde) les glyphes de l'atlas, dans l'ordre des indices
  get glyphList() { return this._glyphList.slice(); }
  // (sonde) la valeur rouge au centre de chaque cellule de la cible privée (en mode
  // ?glyphdebug=index : l'indice de glyphe exact, sans passer par l'écran encodé) ;
  // ligne 0 = bas, comme vUv.y
  readCells(renderer) {
    const t = this._target; if (!t) return null;
    const g = this._CU.uGrid.value, cols = g.x, rows = g.y;
    const buf = new Uint8Array(t.width * t.height * 4);
    renderer.readRenderTargetPixels(t, 0, 0, t.width, t.height, buf);
    const out = new Array(cols * rows);
    for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) { const x = Math.floor((i + 0.5) * t.width / cols), y = Math.floor((j + 0.5) * t.height / rows); out[j * cols + i] = buf[(y * t.width + x) * 4]; }
    return { cols, rows, cells: out };
  }

  dispose() {
    if (this._sceneRef && this._display) { this._display.onBeforeRender = null; this._sceneRef.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    for (const rt of [this._target, this._srcRT, this._prevRT, this._lumTarget]) if (rt) rt.dispose();
    for (const tex of this._atlasCache.values()) tex.dispose();
    this._atlasCache.clear();
    this._src.traverse((o) => { if (o.geometry) o.geometry.dispose(); if (o.material && o.material.dispose) o.material.dispose(); });
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__glyphs === this) window.__glyphs = null;
    this._sceneRef = null;
  }
}
